import { uiText } from "@/lib/i18n/uiText";
// Contextual "You may also like" internal links from a specific fixture to the
// most relevant time-based and market-based prediction landings. Improves
// crawl depth from every match page into the SEO landing cluster and keeps
// users on-site with obviously-related next destinations.

import { Link } from "@/lib/router-compat";
import { findLanding } from "@/lib/predictionLandings";

interface ContextualPredictionLinksProps {
  /** UTC ISO kickoff time — used to pick today/tomorrow/tonight/weekend. */
  kickoffTime?: string | null | undefined;
  /** Optional predicted market (e.g. "1X2", "Over 2.5", "Both Teams to Score"). */
  market?: string | null | undefined;
}

interface Suggestion {
  slug: string;
  label: string;
}

function daysBetweenUtc(fromMs: number, toMs: number): number {
  const oneDay = 86_400_000;
  const a = Math.floor(fromMs / oneDay);
  const b = Math.floor(toMs / oneDay);
  return b - a;
}

/**
 * Choose the timeframe slug that best matches this kickoff. Falls back to
 * "today" when we can't parse the timestamp so users always get a starting
 * point.
 */
function timeframeSlugFor(kickoff?: string | null): string {
  if (!kickoff) return "today";
  const t = Date.parse(kickoff);
  if (!Number.isFinite(t)) return "today";
  const now = Date.now();
  const delta = daysBetweenUtc(now, t);
  if (delta < 0) return "yesterday";
  if (delta === 0) {
    const hour = new Date(t).getUTCHours();
    return hour >= 17 || hour < 3 ? "tonight" : "today";
  }
  if (delta === 1) return "tomorrow";
  const day = new Date(t).getUTCDay();
  if (delta <= 6 && (day === 0 || day === 6 || day === 5)) return "weekend";
  return "tomorrow";
}

/**
 * Market-based sibling landing. We map the coarse `market` string to one of
 * the market×today landings so match pages surface the exact category a user
 * probably wants next.
 */
function marketSlugFor(market?: string | null): string | null {
  if (!market) return null;
  const m = market.toLowerCase();
  if (m.includes("btts") || m.includes("both teams")) return "btts-today";
  if (m.includes("double chance")) return "double-chance-today";
  if (m.includes("draw no bet") || m === "dnb") return "draw-no-bet-today";
  if (m.includes("correct score")) return "correct-score-today";
  if (m.includes("ht/ft") || m.includes("half-time/full-time") || m.includes("half time")) return "ht-ft-today";
  if (m.includes("accumulator") || m.includes("acca")) return "accumulator-today";
  if (m.includes("over 2.5") || m.includes("over 2,5") || m.includes("under 2.5")) return "over-2-5-today";
  if (/\b1x2\b/.test(m) || m.includes("match winner") || m.includes("moneyline")) return "1x2-today";
  if (m.includes("draw")) return "draw-predictions-today";
  return null;
}

export function ContextualPredictionLinks({ kickoffTime, market }: ContextualPredictionLinksProps) {
  const suggestions: Suggestion[] = [];
  const seen = new Set<string>();
  const push = (slug: string) => {
    if (seen.has(slug)) return;
    const l = findLanding(slug);
    if (!l) return;
    seen.add(slug);
    suggestions.push({ slug, label: l.label });
  };

  push(timeframeSlugFor(kickoffTime));
  const mkt = marketSlugFor(market);
  if (mkt) push(mkt);
  // Broad, always-relevant fallbacks.
  push("bonus-picks-today");
  push("premium-picks-today");
  push("high-odds-today");

  if (suggestions.length === 0) return null;

  return (
    <section
      aria-label={uiText("Related prediction pages")}
      data-testid="contextual-prediction-links"
      className="rounded-xl border border-border p-5"
    >
      <h2 className="mb-3 text-lg font-semibold">{uiText("More predictions you might like")}</h2>
      <ul className="flex flex-wrap gap-2">
        {suggestions.slice(0, 6).map((s) => (
          <li key={s.slug}>
            <Link
              to={`/predictions/${s.slug}`}
              className="inline-flex items-center rounded-full border border-border px-3 py-1 text-sm hover:bg-accent hover:text-accent-foreground"
            >
              {uiText(s.label)}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
